import { api } from "./api";

import type {
  AdminDashboardData,
  AdminProduct,
  AdminProductFilters,
  AdminProductListResult,
  ProductStatus
} from "../types/admin";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface PaginatedApiResponse<T>
  extends ApiResponse<T> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getAdminDashboard():
Promise<AdminDashboardData> {
  const response =
    await api.get<ApiResponse<AdminDashboardData>>(
      "/admin/dashboard"
    );

  return response.data.data;
}

export async function getAdminProducts(
  filters: AdminProductFilters
): Promise<AdminProductListResult> {
  const response =
    await api.get<
      PaginatedApiResponse<AdminProduct[]>
    >(
      "/admin/products",
      {
        params: {
          search:
            filters.search || undefined,

          categoryId:
            filters.categoryId || undefined,

          status:
            filters.status || undefined,

          page:
            filters.page ?? 1,

          limit:
            filters.limit ?? 20
        }
      }
    );

  return {
    products: response.data.data,
    pagination: response.data.pagination
  };
}

export async function updateAdminProductStatus(
  productId: string,
  status: ProductStatus
): Promise<AdminProduct> {
  const response =
    await api.patch<ApiResponse<AdminProduct>>(
      `/admin/products/${productId}/status`,
      {
        status
      }
    );

  return response.data.data;
}

export async function getAdminProduct(
  productId: string
): Promise<import("../types/admin").AdminProductDetails> {
  const response = await api.get<
    ApiResponse<
      import("../types/admin").AdminProductDetails
    >
  >(`/admin/products/${productId}`);

  return response.data.data;
}

export async function createAdminProduct(
  input: import("../types/admin").AdminProductInput
): Promise<AdminProduct> {
  const response =
    await api.post<ApiResponse<AdminProduct>>(
      "/admin/products",
      input
    );

  return response.data.data;
}

export async function updateAdminProduct(
  productId: string,
  input: Partial<
    import("../types/admin").AdminProductInput
  >
): Promise<AdminProduct> {
  const response =
    await api.patch<ApiResponse<AdminProduct>>(
      `/admin/products/${productId}`,
      input
    );

  return response.data.data;
}

export async function getAdminCategories(
  filters: import("../types/admin").AdminCategoryFilters
): Promise<import("../types/admin").AdminCategoryListResult> {
  const response = await api.get<
    PaginatedApiResponse<
      import("../types/admin").AdminCategory[]
    >
  >(
    "/admin/categories",
    {
      params: {
        search:
          filters.search || undefined,

        isActive:
          filters.isActive === undefined
            ? undefined
            : String(filters.isActive),

        page:
          filters.page ?? 1,

        limit:
          filters.limit ?? 20
      }
    }
  );

  return {
    categories: response.data.data,
    pagination: response.data.pagination
  };
}

export async function getAdminCategory(
  categoryId: string
): Promise<import("../types/admin").AdminCategory> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminCategory
      >
    >(`/admin/categories/${categoryId}`);

  return response.data.data;
}

export async function createAdminCategory(
  input: import("../types/admin").AdminCategoryInput
): Promise<import("../types/admin").AdminCategory> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminCategory
      >
    >(
      "/admin/categories",
      input
    );

  return response.data.data;
}

export async function updateAdminCategory(
  categoryId: string,
  input: Partial<
    import("../types/admin").AdminCategoryInput
  >
): Promise<import("../types/admin").AdminCategory> {
  const response =
    await api.patch<
      ApiResponse<
        import("../types/admin").AdminCategory
      >
    >(
      `/admin/categories/${categoryId}`,
      input
    );

  return response.data.data;
}

export async function updateAdminCategoryStatus(
  categoryId: string,
  isActive: boolean
): Promise<import("../types/admin").AdminCategory> {
  const response =
    await api.patch<
      ApiResponse<
        import("../types/admin").AdminCategory
      >
    >(
      `/admin/categories/${categoryId}/status`,
      {
        isActive
      }
    );

  return response.data.data;
}

export async function requestProductImageUpload(
  productId: string,
  input: import("../types/admin").ProductImageUploadRequest
): Promise<import("../types/admin").ProductImagePresignResult> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").ProductImagePresignResult
      >
    >(
      `/admin/products/${productId}/images/presign`,
      input
    );

  return response.data.data;
}

export async function uploadFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percentage: number) => void
): Promise<void> {
  await fetch(uploadUrl, {
    method: "PUT",

    headers: {
      "Content-Type": contentType
    },

    body: file
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(
        `S3 upload failed with status ${response.status}`
      );
    }

    onProgress?.(100);
  });
}

export async function confirmProductImage(
  productId: string,
  input: import("../types/admin").ConfirmProductImageInput
): Promise<import("../types/admin").AdminProductImage> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminProductImage
      >
    >(
      `/admin/products/${productId}/images`,
      input
    );

  return response.data.data;
}

export async function setPrimaryProductImage(
  productId: string,
  imageId: string
): Promise<import("../types/admin").AdminProductImage> {
  const response =
    await api.patch<
      ApiResponse<
        import("../types/admin").AdminProductImage
      >
    >(
      `/admin/products/${productId}/images/${imageId}/primary`
    );

  return response.data.data;
}

export async function deleteProductImage(
  productId: string,
  imageId: string
): Promise<void> {
  await api.delete(
    `/admin/products/${productId}/images/${imageId}`
  );
}

export function getProductImageUrl(
  imageId: string
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  return `${baseUrl}/media/product-images/${imageId}`;
}

export async function requestCategoryImageUpload(
  categoryId: string,
  input: import("../types/admin").CategoryImageUploadRequest
): Promise<import("../types/admin").CategoryImagePresignResult> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").CategoryImagePresignResult
      >
    >(
      `/admin/categories/${categoryId}/image/presign`,
      input
    );

  return response.data.data;
}

export async function confirmCategoryImage(
  categoryId: string,
  objectKey: string
): Promise<import("../types/admin").AdminCategory> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminCategory
      >
    >(
      `/admin/categories/${categoryId}/image`,
      {
        objectKey
      }
    );

  return response.data.data;
}

export async function deleteCategoryImage(
  categoryId: string
): Promise<void> {
  await api.delete(
    `/admin/categories/${categoryId}/image`
  );
}

export function getCategoryImageUrl(
  categoryId: string
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  return `${baseUrl}/media/category-images/${categoryId}`;
}

export async function getAdminHomeHeroes():
Promise<import("../types/admin").HomeHero[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").HomeHero[]
      >
    >(
      "/admin/home/hero"
    );

  return response.data.data;
}

export async function createHomeHero(
  input: import("../types/admin").HomeHeroInput
): Promise<import("../types/admin").HomeHero> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").HomeHero
      >
    >(
      "/admin/home/hero",
      input
    );

  return response.data.data;
}

export async function updateHomeHero(
  id: string,
  input: Partial<
    import("../types/admin").HomeHeroInput
  >
): Promise<import("../types/admin").HomeHero> {
  const response =
    await api.patch<
      ApiResponse<
        import("../types/admin").HomeHero
      >
    >(
      `/admin/home/hero/${id}`,
      input
    );

  return response.data.data;
}

export async function requestHomeHeroImageUpload(
  heroId: string,
  variant: "desktop" | "mobile",
  file: File
): Promise<
  import("../types/admin").HeroImagePresignResult
> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").HeroImagePresignResult
      >
    >(
      `/admin/home/hero/${heroId}/image/presign`,
      {
        variant,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      }
    );

  return response.data.data;
}

export async function confirmHomeHeroImage(
  heroId: string,
  variant: "desktop" | "mobile",
  objectKey: string
): Promise<import("../types/admin").HomeHero> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").HomeHero
      >
    >(
      `/admin/home/hero/${heroId}/image`,
      {
        variant,
        objectKey
      }
    );

  return response.data.data;
}

export function getHomeHeroImageUrl(
  heroId: string,
  variant: "desktop" | "mobile",
  version?: string | null
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  const url =
    `${baseUrl}/media/home-hero/${heroId}/${variant}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminHomeSections():
Promise<import("../types/admin").HomeSection[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").HomeSection[]
      >
    >(
      "/admin/home/sections"
    );

  return response.data.data;
}

export async function updateAdminHomeSections(
  sections:
    import("../types/admin").HomeSectionInput[]
): Promise<
  import("../types/admin").HomeSection[]
> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").HomeSection[]
      >
    >(
      "/admin/home/sections",
      {
        sections
      }
    );

  return response.data.data;
}

export async function getAdminMakingRini():
Promise<import("../types/admin").MakingRini> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").MakingRini
      >
    >(
      "/admin/home/making-rini"
    );

  return response.data.data;
}

export async function updateAdminMakingRini(
  input: import("../types/admin").MakingRiniInput
): Promise<import("../types/admin").MakingRini> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").MakingRini
      >
    >(
      "/admin/home/making-rini",
      input
    );

  return response.data.data;
}

export async function requestMakingRiniMediaUpload(
  mediaType: "poster" | "video",
  file: File
): Promise<
  import("../types/admin").MakingRiniMediaPresignResult
> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").MakingRiniMediaPresignResult
      >
    >(
      "/admin/home/making-rini/media/presign",
      {
        mediaType,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      }
    );

  return response.data.data;
}

export async function confirmMakingRiniMedia(
  mediaType: "poster" | "video",
  objectKey: string
): Promise<import("../types/admin").MakingRini> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").MakingRini
      >
    >(
      "/admin/home/making-rini/media",
      {
        mediaType,
        objectKey
      }
    );

  return response.data.data;
}

export function getMakingRiniMediaUrl(
  makingRiniId: string,
  mediaType: "poster" | "video",
  version?: string | null
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  const url =
    `${baseUrl}/media/making-rini/${makingRiniId}/${mediaType}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminIngredients():
Promise<import("../types/admin").AdminIngredient[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminIngredient[]
      >
    >(
      "/admin/ingredients"
    );

  return response.data.data;
}

export async function getAdminIngredient(
  id: string
): Promise<import("../types/admin").AdminIngredient> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminIngredient
      >
    >(
      `/admin/ingredients/${id}`
    );

  return response.data.data;
}

export async function createAdminIngredient(
  input:
    import("../types/admin").AdminIngredientInput
): Promise<import("../types/admin").AdminIngredient> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminIngredient
      >
    >(
      "/admin/ingredients",
      input
    );

  return response.data.data;
}

export async function updateAdminIngredient(
  id: string,
  input:
    import("../types/admin").AdminIngredientInput
): Promise<import("../types/admin").AdminIngredient> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").AdminIngredient
      >
    >(
      `/admin/ingredients/${id}`,
      input
    );

  return response.data.data;
}

export async function deleteAdminIngredient(
  id: string
): Promise<void> {
  await api.delete(
    `/admin/ingredients/${id}`
  );
}

export async function requestIngredientImageUpload(
  ingredientId: string,
  file: File
): Promise<
  import("../types/admin").IngredientImagePresignResult
> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").IngredientImagePresignResult
      >
    >(
      `/admin/ingredients/${ingredientId}/image/presign`,
      {
        fileName:
          file.name,

        contentType:
          file.type,

        fileSize:
          file.size
      }
    );

  return response.data.data;
}

export async function confirmIngredientImage(
  ingredientId: string,
  objectKey: string
): Promise<import("../types/admin").AdminIngredient> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminIngredient
      >
    >(
      `/admin/ingredients/${ingredientId}/image`,
      {
        objectKey
      }
    );

  return response.data.data;
}

export async function deleteIngredientImage(
  ingredientId: string
): Promise<import("../types/admin").AdminIngredient> {
  const response =
    await api.delete<
      ApiResponse<
        import("../types/admin").AdminIngredient
      >
    >(
      `/admin/ingredients/${ingredientId}/image`
    );

  return response.data.data;
}

export function getIngredientImageUrl(
  ingredientId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/ingredient-images/${ingredientId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminTestimonials():
Promise<import("../types/admin").AdminTestimonial[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminTestimonial[]
      >
    >(
      "/admin/testimonials"
    );

  return response.data.data;
}

export async function getAdminTestimonial(
  id: string
): Promise<import("../types/admin").AdminTestimonial> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminTestimonial
      >
    >(
      `/admin/testimonials/${id}`
    );

  return response.data.data;
}

export async function createAdminTestimonial(
  input:
    import("../types/admin").AdminTestimonialInput
): Promise<import("../types/admin").AdminTestimonial> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminTestimonial
      >
    >(
      "/admin/testimonials",
      input
    );

  return response.data.data;
}

export async function updateAdminTestimonial(
  id: string,
  input:
    import("../types/admin").AdminTestimonialInput
): Promise<import("../types/admin").AdminTestimonial> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").AdminTestimonial
      >
    >(
      `/admin/testimonials/${id}`,
      input
    );

  return response.data.data;
}

export async function deleteAdminTestimonial(
  id: string
): Promise<void> {
  await api.delete(
    `/admin/testimonials/${id}`
  );
}

export async function requestTestimonialImageUpload(
  testimonialId: string,
  file: File
): Promise<
  import("../types/admin").TestimonialImagePresignResult
> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").TestimonialImagePresignResult
      >
    >(
      `/admin/testimonials/${testimonialId}/image/presign`,
      {
        fileName:
          file.name,

        contentType:
          file.type,

        fileSize:
          file.size
      }
    );

  return response.data.data;
}

export async function confirmTestimonialImage(
  testimonialId: string,
  objectKey: string
): Promise<import("../types/admin").AdminTestimonial> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminTestimonial
      >
    >(
      `/admin/testimonials/${testimonialId}/image`,
      {
        objectKey
      }
    );

  return response.data.data;
}

export async function deleteTestimonialImage(
  testimonialId: string
): Promise<import("../types/admin").AdminTestimonial> {
  const response =
    await api.delete<
      ApiResponse<
        import("../types/admin").AdminTestimonial
      >
    >(
      `/admin/testimonials/${testimonialId}/image`
    );

  return response.data.data;
}

export function getTestimonialImageUrl(
  testimonialId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/testimonial-images/${testimonialId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminInstagramPosts():
Promise<import("../types/admin").AdminInstagramPost[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminInstagramPost[]
      >
    >(
      "/admin/instagram"
    );

  return response.data.data;
}

export async function getAdminInstagramPost(
  id: string
): Promise<import("../types/admin").AdminInstagramPost> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminInstagramPost
      >
    >(
      `/admin/instagram/${id}`
    );

  return response.data.data;
}

export async function createAdminInstagramPost(
  input:
    import("../types/admin").AdminInstagramPostInput
): Promise<import("../types/admin").AdminInstagramPost> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminInstagramPost
      >
    >(
      "/admin/instagram",
      input
    );

  return response.data.data;
}

export async function updateAdminInstagramPost(
  id: string,
  input:
    import("../types/admin").AdminInstagramPostInput
): Promise<import("../types/admin").AdminInstagramPost> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").AdminInstagramPost
      >
    >(
      `/admin/instagram/${id}`,
      input
    );

  return response.data.data;
}

export async function deleteAdminInstagramPost(
  id: string
): Promise<void> {
  await api.delete(
    `/admin/instagram/${id}`
  );
}

export async function requestInstagramImageUpload(
  postId: string,
  file: File
): Promise<
  import("../types/admin").InstagramImagePresignResult
> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").InstagramImagePresignResult
      >
    >(
      `/admin/instagram/${postId}/image/presign`,
      {
        fileName:
          file.name,

        contentType:
          file.type,

        fileSize:
          file.size
      }
    );

  return response.data.data;
}

export async function confirmInstagramImage(
  postId: string,
  objectKey: string
): Promise<import("../types/admin").AdminInstagramPost> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminInstagramPost
      >
    >(
      `/admin/instagram/${postId}/image`,
      {
        objectKey
      }
    );

  return response.data.data;
}

export async function deleteInstagramImage(
  postId: string
): Promise<import("../types/admin").AdminInstagramPost> {
  const response =
    await api.delete<
      ApiResponse<
        import("../types/admin").AdminInstagramPost
      >
    >(
      `/admin/instagram/${postId}/image`
    );

  return response.data.data;
}

export function getInstagramImageUrl(
  postId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/instagram-images/${postId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminGiftHamperSections():
Promise<import("../types/admin").AdminGiftHamperSection[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminGiftHamperSection[]
      >
    >(
      "/admin/home/gift-hamper"
    );

  return response.data.data;
}

export async function createGiftHamperSection(
  input: import("../types/admin").AdminGiftHamperSectionInput
): Promise<import("../types/admin").AdminGiftHamperSection> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminGiftHamperSection
      >
    >(
      "/admin/home/gift-hamper",
      input
    );

  return response.data.data;
}

export async function updateGiftHamperSection(
  id: string,
  input: import("../types/admin").AdminGiftHamperSectionInput
): Promise<import("../types/admin").AdminGiftHamperSection> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").AdminGiftHamperSection
      >
    >(
      `/admin/home/gift-hamper/${id}`,
      input
    );

  return response.data.data;
}

export async function requestGiftHamperImageUpload(
  sectionId: string,
  variant: "desktop" | "mobile",
  file: File
): Promise<import("../types/admin").GiftHamperImagePresignResult> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").GiftHamperImagePresignResult
      >
    >(
      `/admin/home/gift-hamper/${sectionId}/${variant}/presign`,
      {
        fileName:
          file.name,

        contentType:
          file.type,

        fileSize:
          file.size
      }
    );

  return response.data.data;
}

export async function confirmGiftHamperImage(
  sectionId: string,
  variant: "desktop" | "mobile",
  objectKey: string
): Promise<import("../types/admin").AdminGiftHamperSection> {
  const response =
    await api.post<
      ApiResponse<
        import("../types/admin").AdminGiftHamperSection
      >
    >(
      `/admin/home/gift-hamper/${sectionId}/${variant}`,
      {
        objectKey
      }
    );

  return response.data.data;
}

export async function deleteGiftHamperImage(
  sectionId: string,
  variant: "desktop" | "mobile"
): Promise<import("../types/admin").AdminGiftHamperSection> {
  const response =
    await api.delete<
      ApiResponse<
        import("../types/admin").AdminGiftHamperSection
      >
    >(
      `/admin/home/gift-hamper/${sectionId}/${variant}`
    );

  return response.data.data;
}

export function getGiftHamperImageUrl(
  sectionId: string,
  variant: "desktop" | "mobile",
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/gift-hampers/${sectionId}/${variant}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getAdminGiftHamperItems(
  productId: string
): Promise<
  import("../types/admin").AdminGiftHamperItem[]
> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminGiftHamperItem[]
      >
    >(
      `/admin/products/${productId}/hamper-items`
    );

  return response.data.data;
}

export async function updateAdminGiftHamperItems(
  productId: string,
  items:
    import("../types/admin").AdminGiftHamperItemInput[]
): Promise<
  import("../types/admin").AdminGiftHamperItem[]
> {
  const response =
    await api.put<
      ApiResponse<
        import("../types/admin").AdminGiftHamperItem[]
      >
    >(
      `/admin/products/${productId}/hamper-items`,
      {
        items
      }
    );

  return response.data.data;
}



export async function getAdminOrders(
  params?: {
    search?: string;
    orderStatus?: string;
    paymentStatus?: string;
    page?: number;
    limit?: number;
  }
): Promise<
  import("../types/admin").AdminOrderListResult
> {
  const response = await api.get(
    "/admin/orders",
    {
      params
    }
  );

  return {
    orders: response.data.data,
    pagination: response.data.pagination
  };
}

export async function updateAdminOrder(
  orderId: string,
  input: {
    orderStatus?: import("../types/admin").AdminOrderStatus;
    paymentStatus?: import("../types/admin").AdminPaymentStatus;
    shippingAmount?: number;
  }
): Promise<
  import("../types/admin").AdminOrder
> {
  const response = await api.patch(
    `/admin/orders/${orderId}`,
    input
  );

  return response.data.data;
}


export async function getAdminInventory(
  search?: string
): Promise<
  import("../types/admin").AdminInventoryResult
> {
  const response = await api.get(
    "/admin/inventory",
    {
      params:
        search
          ? {
              search
            }
          : undefined
    }
  );

  return response.data.data;
}

export async function getAdminInventoryMovements(
  limit = 100
): Promise<
  import("../types/admin").AdminInventoryMovement[]
> {
  const response = await api.get(
    "/admin/inventory/movements",
    {
      params: {
        limit
      }
    }
  );

  return response.data.data;
}

export async function adjustAdminInventory(
  productId: string,
  input: {
    quantityChange: number;
    reason:
      import("../types/admin").AdminInventoryReason;
    note?: string | null;
  }
) {
  const response = await api.patch(
    `/admin/inventory/${productId}/adjust`,
    input
  );

  return response.data.data;
}


export async function getAdminCustomers(
  search?: string
): Promise<
  import("../types/admin").AdminCustomerResult
> {
  const response = await api.get(
    "/admin/customers",
    {
      params:
        search
          ? {
              search
            }
          : undefined
    }
  );

  return response.data.data;
}


export async function getAdminBusinessSettings():
Promise<
  import("../types/admin").AdminBusinessSettings
> {
  const response = await api.get(
    "/admin/settings"
  );

  return response.data.data;
}

export async function updateAdminBusinessSettings(
  input: {
    businessName: string;
    brandName: string;

    primaryPhone?: string | null;
    supportPhone?: string | null;
    whatsappNumber?: string | null;
    supportEmail?: string | null;

    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country: string;

    gstRegistered: boolean;
    gstNumber?: string | null;
    cin?: string | null;

    instagramUrl?: string | null;
    facebookUrl?: string | null;
    youtubeUrl?: string | null;
    websiteUrl?: string | null;

    allowCustomShipping: boolean;
    defaultShippingAmount: number;
    freeShippingThreshold?: number | null;

    whatsappFooter?: string | null;
    emailFooter?: string | null;
  }
): Promise<
  import("../types/admin").AdminBusinessSettings
> {
  const response = await api.put(
    "/admin/settings",
    input
  );

  return response.data.data;
}


export async function getAdminOrderWhatsAppMessage(
  orderId: string,
  event:
    import("../types/admin").AdminWhatsAppEvent
): Promise<
  import("../types/admin").AdminWhatsAppMessage
> {
  const response = await api.get(
    `/admin/message-templates/order/${orderId}/${event}`
  );

  return response.data.data;
}

export async function getAdminMessageTemplates():
Promise<
  import("../types/admin").AdminMessageTemplate[]
> {
  const response = await api.get(
    "/admin/message-templates"
  );

  return response.data.data;
}

export async function updateAdminMessageTemplate(
  event: import("../types/admin").AdminWhatsAppEvent,
  input: {
    title: string;
    message: string;
    enabled: boolean;
  }
): Promise<
  import("../types/admin").AdminMessageTemplate
> {
  const response = await api.put(
    `/admin/message-templates/${event}`,
    input
  );

  return response.data.data;
}
